import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { paymentAPI, tutoringRequestAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  CheckCircle, Loader2, ArrowRight, FileText, 
  User, Mail, Phone, BookOpen, Calendar, Target, MessageSquare
} from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const sessionId = searchParams.get('session_id');

  const [formData, setFormData] = useState({
    student_name: '',
    student_email: '',
    parent_name: '',
    parent_email: '',
    phone: '',
    subjects: [],
    grade_level: '',
    preferred_schedule: '',
    specific_goals: '',
    learning_challenges: '',
    tutor_preferences: '',
    additional_notes: ''
  });

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentAPI.getPackageStatus(sessionId);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Pre-fill form with payment data
        setFormData(prev => ({
          ...prev,
          student_name: response.data.student_name || '',
          student_email: response.data.student_email || '',
          subjects: response.data.subjects || [],
          grade_level: response.data.student_level === 'highSchool' ? 'High School' : 'University'
        }));
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!formData.student_name || !formData.student_email || !formData.preferred_schedule) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingRequest(true);
    try {
      await tutoringRequestAPI.create({
        payment_id: paymentData.payment_id,
        ...formData
      });
      toast.success('Tutoring request submitted successfully!');
      setShowRequestForm(false);
      setStatus('request_submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const scheduleOptions = [
    'Weekday mornings (9am-12pm)',
    'Weekday afternoons (12pm-4pm)',
    'Weekday evenings (4pm-9pm)',
    'Saturday mornings',
    'Saturday afternoons',
    'Sunday any time',
    'Flexible - any time works'
  ];

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-6" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          {status === 'loading' && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
            >
              <Loader2 className="h-16 w-16 animate-spin text-indigo-700 mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                Processing Payment
              </h1>
              <p className="text-slate-600">Please wait while we confirm your payment...</p>
            </motion.div>
          )}

          {status === 'success' && !showRequestForm && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-stone-100 shadow-card">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    Payment Successful!
                  </h1>
                  <p className="text-slate-600 mb-6">
                    Thank you for your purchase! To help us match you with the perfect tutor, please complete a brief request form.
                  </p>
                  
                  {paymentData && (
                    <div className="bg-stone-50 rounded-lg p-4 mb-6 text-left">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-500">Package</span>
                        <span className="font-semibold">{paymentData.package_name || 'Tutoring Package'}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-500">Hours</span>
                        <span className="font-semibold">{paymentData.hours || 'N/A'} hours</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Amount Paid</span>
                        <span className="font-semibold">${paymentData.amount || (paymentData.amount_total / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => setShowRequestForm(true)}
                      className="rounded-full bg-indigo-700 hover:bg-indigo-800"
                      data-testid="start-request-form-btn"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Complete Request Form
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      className="rounded-full"
                    >
                      Skip for Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {status === 'success' && showRequestForm && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-stone-100 shadow-card">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-heading text-2xl">Tutoring Request Form</CardTitle>
                  <p className="text-slate-600 text-sm">Help us match you with the perfect tutor</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitRequest} className="space-y-6">
                    {/* Student Information */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-slate-900 flex items-center gap-2">
                        <User className="h-4 w-4 text-indigo-600" />
                        Student Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="student_name">Student Name *</Label>
                          <Input
                            id="student_name"
                            name="student_name"
                            value={formData.student_name}
                            onChange={handleInputChange}
                            placeholder="Full name"
                            required
                            data-testid="student-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="student_email">Student Email *</Label>
                          <Input
                            id="student_email"
                            name="student_email"
                            type="email"
                            value={formData.student_email}
                            onChange={handleInputChange}
                            placeholder="email@example.com"
                            required
                            data-testid="student-email-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone">Phone (Optional)</Label>
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+1 (555) 000-0000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="grade_level">Grade Level</Label>
                          <Input
                            id="grade_level"
                            name="grade_level"
                            value={formData.grade_level}
                            onChange={handleInputChange}
                            placeholder="e.g., Grade 11, University Year 2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Parent/Guardian Information */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-slate-900 flex items-center gap-2">
                        <Mail className="h-4 w-4 text-indigo-600" />
                        Parent/Guardian (if applicable)
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="parent_name">Parent/Guardian Name</Label>
                          <Input
                            id="parent_name"
                            name="parent_name"
                            value={formData.parent_name}
                            onChange={handleInputChange}
                            placeholder="Full name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="parent_email">Parent/Guardian Email</Label>
                          <Input
                            id="parent_email"
                            name="parent_email"
                            type="email"
                            value={formData.parent_email}
                            onChange={handleInputChange}
                            placeholder="parent@example.com"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Subjects & Schedule */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-slate-900 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-indigo-600" />
                        Subjects & Schedule
                      </h3>
                      <div>
                        <Label htmlFor="subjects_display">Subjects</Label>
                        <Input
                          id="subjects_display"
                          value={formData.subjects?.join(', ') || ''}
                          disabled
                          className="bg-stone-50"
                        />
                        <p className="text-xs text-slate-500 mt-1">Based on your package selection</p>
                      </div>
                      <div>
                        <Label htmlFor="preferred_schedule">Preferred Schedule *</Label>
                        <select
                          id="preferred_schedule"
                          name="preferred_schedule"
                          value={formData.preferred_schedule}
                          onChange={handleInputChange}
                          required
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="schedule-select"
                        >
                          <option value="">Select preferred time...</option>
                          {scheduleOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Goals & Preferences */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-slate-900 flex items-center gap-2">
                        <Target className="h-4 w-4 text-indigo-600" />
                        Goals & Preferences
                      </h3>
                      <div>
                        <Label htmlFor="specific_goals">Specific Learning Goals</Label>
                        <Textarea
                          id="specific_goals"
                          name="specific_goals"
                          value={formData.specific_goals}
                          onChange={handleInputChange}
                          placeholder="e.g., Prepare for SAT, improve calculus grades, understand organic chemistry..."
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label htmlFor="learning_challenges">Learning Challenges (Optional)</Label>
                        <Textarea
                          id="learning_challenges"
                          name="learning_challenges"
                          value={formData.learning_challenges}
                          onChange={handleInputChange}
                          placeholder="Any specific areas you struggle with or learning accommodations needed..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label htmlFor="tutor_preferences">Tutor Preferences (Optional)</Label>
                        <Input
                          id="tutor_preferences"
                          name="tutor_preferences"
                          value={formData.tutor_preferences}
                          onChange={handleInputChange}
                          placeholder="e.g., Female tutor preferred, native English speaker..."
                        />
                      </div>
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <Label htmlFor="additional_notes" className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-indigo-600" />
                        Additional Notes
                      </Label>
                      <Textarea
                        id="additional_notes"
                        name="additional_notes"
                        value={formData.additional_notes}
                        onChange={handleInputChange}
                        placeholder="Any other information that would help us match you with the right tutor..."
                        rows={3}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowRequestForm(false)}
                        className="rounded-full flex-1"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={submittingRequest}
                        className="rounded-full bg-indigo-700 hover:bg-indigo-800 flex-1"
                        data-testid="submit-request-btn"
                      >
                        {submittingRequest ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>Submit Request</>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {status === 'request_submitted' && (
            <motion.div
              key="submitted"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-stone-100 shadow-card">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    Request Submitted!
                  </h1>
                  <p className="text-slate-600 mb-6">
                    We've received your tutoring request. Our team will review it and match you with the perfect tutor within 24-48 hours. You'll receive an email notification once matched.
                  </p>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="rounded-full bg-slate-900 hover:bg-slate-800"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Card className="border-stone-100 shadow-card">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-10 w-10 text-amber-600" />
                  </div>
                  <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    {status === 'timeout' ? 'Payment Pending' : 'Payment Issue'}
                  </h1>
                  <p className="text-slate-600 mb-6">
                    {status === 'timeout' 
                      ? 'Payment is still being processed. Please check your dashboard for updates.'
                      : 'There was an issue with your payment. Please try again or contact support.'}
                  </p>
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="rounded-full bg-slate-900 hover:bg-slate-800"
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/pricing')}
                      className="rounded-full"
                    >
                      Try Again
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
